import React from 'react';
import {
  PortsInCountryTable,
  UpcomingVesselPortCallsTable,
  VesselPortCallsTable
} from '@components/Tables';
import { DEFAULT_NUM_PORT_CALLS, GEOGRAPHICAL_TYPES } from '@constants';
import { useQueries } from '@tanstack/react-query';
import {
  getActivePorts,
  getCountryDetail,
  getLatestPortCalls,
  getPortCallSummary,
  getPortsDetails,
  getUpcomingPortCalls
} from '@api';
import { useParams } from 'react-router-dom';
import { Grid, Paper, Stack } from '@esgian/esgianui';
import { useSelector } from 'react-redux';
import { getGeneralSlice } from '@store/features';
import { toast } from 'react-toastify';
import { CountryPortsMap } from '@components';
import { PORT_UPCOMING_TITLE } from '@constants/tooltips';
import PropTypes from 'prop-types';

const CountryOverviewSection = ({ countryPortDetailsQuery }) => {
  const params = useParams();
  const { segment, operatorType, commercialCategoryType } = useSelector(getGeneralSlice);

  const overviewQueries = useQueries({
    queries: [
      {
        placeholderData: {},
        queryKey: ['countryDetailsOverview', params.countryId],
        queryFn: ({ signal }) =>
          getCountryDetail(signal, Number(params.countryId)).then((res) => {
            return { ...res, ports: res.ports.filter(({ active }) => active) };
          })
      },
      {
        placeholderData: null,
        queryKey: [
          'countryLastPortCallsOverview',
          {
            geographicalType: GEOGRAPHICAL_TYPES.COUNTRY,
            segmentTypeId: segment.id,
            operatorTypeId: operatorType,
            numberOfPortCalls: DEFAULT_NUM_PORT_CALLS,
            commercialCategoryId: commercialCategoryType,
            geoIds: [Number(params.countryId)]
          }
        ],
        queryFn: ({ signal, queryKey }) => getLatestPortCalls(queryKey[1], signal)
      },
      {
        placeholderData: null,
        queryKey: [
          'countryLastPortCallsOverview',
          {
            numberOfIncomingEntries: DEFAULT_NUM_PORT_CALLS,
            segmentTypeId: segment.id,
            operatorTypeId: operatorType,
            commercialCategoryId: commercialCategoryType,
            geoIds: [Number(params.countryId)],
            geoType: GEOGRAPHICAL_TYPES.COUNTRY
          }
        ],
        queryFn: ({ signal, queryKey }) => getUpcomingPortCalls(queryKey[1], signal)
      }
    ]
  });

  const { name: countryName } = overviewQueries[0].data;

  return (
    <Stack>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <PortsInCountryTable
            countryName={countryName}
            loading={overviewQueries[0].isFetching || countryPortDetailsQuery.isFetching}
            countryPortsData={countryPortDetailsQuery.data}
          />
        </Grid>
        <Grid item xs={6}>
          <Paper>
            <CountryPortsMap
              height={'40vh'}
              loading={countryPortDetailsQuery.isFetching}
              portData={countryPortDetailsQuery.data}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <VesselPortCallsTable
            loading={overviewQueries[1].isFetching}
            name={countryName}
            geoId={Number(params.countryId)}
            title={'Most Recent Vessels Port Calls'}
            portCalls={overviewQueries[1].data}
            geographicalType={GEOGRAPHICAL_TYPES.COUNTRY}
            labels={[
              'Vessel',
              'IMO',
              'Operator',
              'Port',
              'Country',
              'UNLOCODE',
              'Arrival',
              'Departure',
              'Port Time (hours)'
            ]}
            headers={[
              'vesselName',
              'imo',
              'operatorShortName',
              'portName',
              'countryName',
              'portCode',
              'startDate',
              'endDate',
              'timeInPort'
            ]}
            allowDownloadAll={true}
          />
        </Grid>
        <Grid item xs={12}>
          <UpcomingVesselPortCallsTable
            assetLevel={'port'}
            title={'Upcoming Vessels Port Calls'}
            upcomingPortCalls={overviewQueries[2].data}
            loading={overviewQueries[2].isFetching}
            name={countryName}
            titleWithTooltip
            tooltipText={PORT_UPCOMING_TITLE}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

CountryOverviewSection.propTypes = {
  countryPortDetailsQuery: PropTypes.object
};

export default CountryOverviewSection;
